import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { formatGBP } from "../lib/format";
import { toast } from "sonner";
import { Truck, Storefront, Lock, ArrowLeft } from "@phosphor-icons/react";
import { WhatsAppShareButton } from "../components/WhatsAppWidget";

export default function Checkout() {
  const { user } = useAuth();
  const { items, clear } = useCart();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const defaultAddress = (user?.addresses || []).find((a) => a.is_default) || user?.addresses?.[0];

  const [form, setForm] = useState({
    label: defaultAddress?.label || "Home",
    line1: defaultAddress?.line1 || "",
    line2: defaultAddress?.line2 || "",
    city: defaultAddress?.city || "",
    postcode: defaultAddress?.postcode || "",
    delivery_notes: "",
  });

  const fetchPreview = useCallback(async () => {
    if (!form.postcode || !form.line1 || !form.city) {
      setPreview(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/orders/checkout-preview", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        delivery_address: {
          label: form.label,
          line1: form.line1,
          line2: form.line2,
          city: form.city,
          postcode: form.postcode,
          is_default: false,
        },
        origin_url: window.location.origin,
        delivery_notes: form.delivery_notes,
      });
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not calculate totals");
      setPreview(null);
    }
    setLoading(false);
  }, [form, items]);

  useEffect(() => {
    if (items.length === 0) {
      navigate("/cart");
      return;
    }
    fetchPreview();
    // Run only on mount: we intentionally don't refetch as the user types — they hit "Update delivery & totals"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePay = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/orders/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        delivery_address: {
          label: form.label,
          line1: form.line1,
          line2: form.line2,
          city: form.city,
          postcode: form.postcode,
          is_default: false,
        },
        origin_url: window.location.origin,
        delivery_notes: form.delivery_notes,
      });
      if (data.url) {
        // Keep cart; clear on success page
        window.location.href = data.url;
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Checkout failed");
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setSubmitting(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-4 md:px-8 lg:px-12" data-testid="checkout-page">
      <Link to="/cart" className="inline-flex items-center gap-2 text-xs font-bold text-[#5C5C54] hover:text-[#A94438] mb-6">
        <ArrowLeft size={14} weight="bold" /> Back to cart
      </Link>
      <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-2">Secure checkout</h1>
      <p className="text-sm text-[#5C5C54] mb-6">Paying with Stripe · GDPR-compliant · Your card is never stored on AfriPantry.</p>

      <div className="mb-6 p-3 md:p-4 rounded-2xl bg-[#E4A951]/10 border border-[#E4A951]/40 flex items-start gap-3" data-testid="checkout-separate-delivery-notice">
        <Truck size={18} weight="duotone" className="text-[#8A6A1F] mt-0.5 flex-shrink-0" />
        <p className="text-xs md:text-sm text-[#5C5C54]">
          <span className="font-bold text-[#1A1A18]">Items from different vendors may arrive separately</span> and may have different delivery times. Delivery times vary by vendor.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_420px] gap-6 lg:gap-8">
        {/* FORM */}
        <div className="card-surface p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-5">Delivery address</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Address line 1</label>
              <input data-testid="checkout-line1" className="input-field" value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Address line 2 (optional)</label>
              <input data-testid="checkout-line2" className="input-field" value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">City</label>
              <input data-testid="checkout-city" className="input-field" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Postcode</label>
              <input data-testid="checkout-postcode" className="input-field uppercase" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value.toUpperCase() })} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Delivery notes (optional)</label>
              <textarea data-testid="checkout-notes" rows={2} className="input-field resize-none" value={form.delivery_notes} onChange={(e) => setForm({ ...form, delivery_notes: e.target.value })} />
            </div>
          </div>
          <button
            data-testid="checkout-recalculate"
            onClick={fetchPreview}
            className="btn-secondary text-xs mt-4"
          >
            Update delivery & totals
          </button>
        </div>

        {/* SUMMARY */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-4">
          <div className="card-surface p-6" data-testid="checkout-summary">
            <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Your order</h2>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-[#A94438]/10 border border-[#A94438]/30 text-xs text-[#A94438] font-semibold">{error}</div>
            )}

            {loading ? (
              <p className="text-xs text-[#5C5C54]">Calculating totals...</p>
            ) : preview ? (
              <>
                <p className="text-xs text-[#5C5C54] mb-3" data-testid="vendor-count-text">
                  You are ordering from <span className="font-bold text-[#1A1A18]">{preview.vendor_count} vendor{preview.vendor_count === 1 ? "" : "s"}</span>.
                </p>

                <div className="space-y-3 mb-5">
                  {preview.groups.map((g) => (
                    <div key={g.vendor_id} className="border border-[#E5E2D9] rounded-xl p-3 bg-[#F9F7F3]">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-heading font-bold text-sm flex items-center gap-1.5">
                          <Storefront size={14} weight="duotone" /> {g.vendor_name}
                        </p>
                        <span className="text-xs text-[#5C5C54]">~{g.estimated_delivery_days}d</span>
                      </div>
                      <div className="space-y-1 text-xs">
                        {g.items.map((it) => (
                          <div key={it.product_id} className="flex justify-between text-[#5C5C54]">
                            <span className="truncate pr-2">{it.quantity} × {it.name}</span>
                            <span className="font-semibold text-[#1A1A18] flex-shrink-0">{formatGBP(it.price * it.quantity)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 pt-2 border-t border-[#E5E2D9] text-xs space-y-1">
                        <div className="flex justify-between text-[#5C5C54]"><span>Subtotal</span><span>{formatGBP(g.subtotal)}</span></div>
                        <div className="flex justify-between text-[#5C5C54]"><span>Delivery</span><span>{formatGBP(g.delivery_fee)}</span></div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-[#E5E2D9] pt-4 space-y-1.5 text-sm">
                  <div className="flex justify-between text-[#5C5C54]"><span>Subtotal</span><span className="font-bold text-[#1A1A18]">{formatGBP(preview.cart_subtotal)}</span></div>
                  <div className="flex justify-between text-[#5C5C54]"><span>Delivery</span><span className="font-bold text-[#1A1A18]">{formatGBP(preview.cart_delivery)}</span></div>
                  <div className="flex justify-between text-[#5C5C54]"><span>Service fee</span><span className="font-bold text-[#1A1A18]">{formatGBP(preview.service_fee)}</span></div>
                  <div className="flex justify-between items-baseline pt-2 mt-2 border-t border-[#E5E2D9]">
                    <span className="text-xs font-bold tracking-wider uppercase">Total</span>
                    <span className="font-heading text-2xl font-black" data-testid="checkout-total-amount">{formatGBP(preview.total)}</span>
                  </div>
                </div>

                <button
                  data-testid="pay-now-btn"
                  disabled={submitting}
                  onClick={handlePay}
                  className="btn-primary w-full mt-6 gap-2 disabled:opacity-50"
                >
                  <Lock size={16} weight="duotone" /> {submitting ? "Redirecting..." : "Pay with Stripe"}
                </button>

                <p className="text-[10px] text-center text-[#5C5C54]/80 mt-3">
                  By placing this order you agree to AfriPantry's Terms. Test card: 4242 4242 4242 4242
                </p>
              </>
            ) : (
              <p className="text-xs text-[#5C5C54]">Fill in your address to see totals.</p>
            )}
          </div>
          <div className="card-surface p-5 text-center" data-testid="checkout-help-card">
            <p className="text-xs text-[#5C5C54] mb-2">Need help? Chat with us on WhatsApp.</p>
            <WhatsAppShareButton
              message="Hi AfriPantry, I need help with my checkout."
              label="Chat with us on WhatsApp"
              testid="checkout-whatsapp-help"
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
